import type { TaxBreakdown, AmountMode } from '../types/checkin';

const FPT_RATE = 0.04;  // 4%
const TVA_RATE = 0.16;  // 16%
const TOTAL_TAX_RATE = 1 + FPT_RATE + TVA_RATE;  // 1.20

export function roundTo2Decimals(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Calculate tax breakdown from HT amount.
 * Tax-exempt guests: TVA = 0, FPT still applies.
 */
export function calculateFromHT(amountHT: number, isTaxExempt = false): TaxBreakdown {
  const ht = roundTo2Decimals(amountHT);
  const fpt = roundTo2Decimals(ht * FPT_RATE);
  const tva = isTaxExempt ? 0 : roundTo2Decimals(ht * TVA_RATE);
  const ttc = roundTo2Decimals(ht + fpt + tva);

  return {
    amountHT: ht,
    amountFPT: fpt,
    amountTVA: tva,
    amountTTC: ttc,
  };
}

/**
 * Calculate tax breakdown from TTC amount.
 * Tax-exempt guests: TTC = HT (no TVA), FPT still applies.
 */
export function calculateFromTTC(amountTTC: number, isTaxExempt = false): TaxBreakdown {
  const ttc = roundTo2Decimals(amountTTC);
  const taxDivisor = isTaxExempt ? (1 + FPT_RATE) : TOTAL_TAX_RATE;
  const ht = roundTo2Decimals(ttc / taxDivisor);
  const fpt = roundTo2Decimals(ht * FPT_RATE);
  const tva = isTaxExempt ? 0 : roundTo2Decimals(ht * TVA_RATE);

  return {
    amountHT: ht,
    amountFPT: fpt,
    amountTVA: tva,
    amountTTC: ttc,
  };
}

/**
 * Calculate tax breakdown based on amount mode.
 * Pass isTaxExempt=true for guests with tax exemption (TVA waived, FPT kept).
 */
export function calculateTaxes(amount: number, mode: AmountMode, isTaxExempt = false): TaxBreakdown {
  if (mode === 'HT') {
    return calculateFromHT(amount, isTaxExempt);
  } else {
    return calculateFromTTC(amount, isTaxExempt);
  }
}

/**
 * Format currency value for display
 */
export function formatCurrency(value: number): string {
  return value.toFixed(2);
}

/**
 * Parse currency string to number
 */
export function parseCurrency(value: string): number {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}
